import { API } from '../../api';
import { Form, Input, Select, Spin, Tooltip, notification } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';

/**
 * Component for building schedules and jobs
 * Includes the job type, payload, and routing inputs
 */

const JobBuildInputs = props => {
  const { selectedClient, form, inputFields, setInputFields, hasRouteOn, setHasRouteOn } = props;
  const { TextArea } = Input;
  const [inputDescr, setInputDescr] = useState({});
  const [jobTypes, setJobTypes] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [inputFieldsLoading, setInputFieldsLoading] = useState(false);

  const getJobTypeRouting = name => {
    // set jobtype routing
    for (const jobType of jobTypes) {
      if (jobType.value === name) {
        setHasRouteOn(jobType.routing !== null);
      }
    }
  };

  const handleGetInputFields = async () => {
    setInputFieldsLoading(true);
    const jobType = form.getFieldValue('job_type') ?? null;

    getJobTypeRouting(jobType);

    // get client inputFields for selected jobType from DIS API
    try {
      const res = await API.get(`/dis/fields?clientId=${selectedClient.id}&jobType=${jobType}`);
      const fields = {};
      const descr = {};

      for (const field of res.data) {
        fields[field.name] = null;
        descr[field.name] = field.description;
      }

      setInputFields(fields);
      setInputDescr(descr);
    } catch (e) {
      notification.error({
        message: 'Error',
        description: 'Unable to get Input Fields for selected Job Type',
        duration: 3,
        placement: 'topLeft',
      });
    }

    setInputFieldsLoading(false);
  };

  const getJobTypes = async () => {
    try {
      const res = await API.get(`/dis/jobTypes?clientId=${selectedClient.id}`);
      setJobTypes(
        res.data.results.map(jobType => ({
          value: jobType.name,
          label: `${jobType.name}: ${jobType.description}`,
          routing: jobType.routeOn,
        })),
      );
    } catch (e) {
      notification.error({
        message: 'Error',
        description: `Unable to get Job Types for client: ${selectedClient.name}`,
        duration: 3,
        placement: 'topLeft',
      });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await getJobTypes();
      setLoading(false);
    };

    if (selectedClient) fetchData();
  }, [selectedClient]);

  return (
    <>
      <h3>Job Type</h3>
      <Spin spinning={isLoading}>
        <Form.Item
          name="job_type"
          initialValue={null}
          rules={[{ required: true, message: 'Job Type is required!' }]}
        >
          <Select
            showSearch
            style={{ width: '95%' }}
            filterOption={(input, option) =>
              (option?.name ?? '').toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(optionA, optionB) =>
              (optionA?.name ?? '').toLowerCase().localeCompare((optionB?.name ?? '').toLowerCase())
            }
            placeholder="Select Job Type"
            data-cy="jobType"
            options={jobTypes}
            id="select-job-type"
            size="large"
            onChange={handleGetInputFields}
          />
        </Form.Item>

        <h3>Payload</h3>
        <Spin spinning={inputFieldsLoading}>
          {Object.entries(inputFields).length > 0 ? (
            Object.keys(inputFields).map(field => {
              return (
                <Form.Item
                  name={field}
                  label={
                    <div>
                      {field}
                      <Tooltip placement="right" title={inputDescr[field]}>
                        <InfoCircleOutlined style={{ marginLeft: '5px' }} />
                      </Tooltip>
                    </div>
                  }
                  key={field}
                >
                  <Input id={field} style={{ width: '95%' }} />
                </Form.Item>
              );
            })
          ) : (
            <p>No input fields required for selected Job Type</p>
          )}
        </Spin>

        <h3>
          Routing
          <Tooltip
            placement="bottom"
            overlayClassName="extra-wide"
            title="If the Schedule's Job Type has a routeOnField then this field is used to specify
              routing for the schedule.
              Example of valid Routing Object:
              [
                {
                    'fieldToMatch': 'testFileName',
                    'pattern': '(.*).ndjson',
                    'jobType': 'FetchS3AndProcess',
                    'iterationPayload': {
                        'testSchema': 'testNdjsonSchema'
                    }
                },
                {
                    'fieldToMatch': 'testFileName',
                    'pattern': '(.*).csv',
                    'jobType': 'FetchS3AndProcess',
                    'iterationPayload': {
                        'testSchema': 'testCsvSchema'
                    }
                }
              ]"
          >
            <InfoCircleOutlined style={{ marginLeft: '5px' }} />
          </Tooltip>
        </h3>
        {hasRouteOn ? (
          <Form.Item name="routing" initialValue={[]} rules={[{ required: false }]}>
            <TextArea
              rows={1}
              placeholder="Routing"
              data-cy="routing"
              autoSize={{ minRows: 1, maxRows: 500 }}
              style={{ width: '95%' }}
              size="large"
            />
          </Form.Item>
        ) : (
          <p>Selected Job Type does not require Routing</p>
        )}
      </Spin>
    </>
  );
};

export default JobBuildInputs;
